import { dateTime } from '@/_shared';

import type { EmailHistoryQuerySchema } from '../schemas/email-history-query-schema';

export function isScheduledEmailHistory(
  row: Pick<EmailHistoryQuerySchema, 'delivery_status' | 'scheduled_at' | 'sent_at'>
): boolean {
  if (row.delivery_status?.toLowerCase() === 'scheduled') {
    return true;
  }

  return Boolean(row.scheduled_at && !row.sent_at);
}

export function isFutureScheduledEmailHistory(
  row: Pick<EmailHistoryQuerySchema, 'delivery_status' | 'scheduled_at' | 'sent_at'>
): boolean {
  if (row.sent_at) {
    return false;
  }

  const hasScheduledStatus = row.delivery_status?.toLowerCase() === 'scheduled';
  const hasScheduledTimestamp = Boolean(row.scheduled_at);

  if (!hasScheduledStatus && !hasScheduledTimestamp) {
    return false;
  }

  if (!row.scheduled_at) {
    return hasScheduledStatus;
  }

  const scheduled = dateTime.parse(row.scheduled_at);
  if (!scheduled.isValid()) {
    return hasScheduledStatus;
  }

  return scheduled.isAfter(dateTime.now());
}

export function formatEmailHistorySentCell(row: Pick<EmailHistoryQuerySchema, 'scheduled_at' | 'sent_at'>): string {
  if (!row.sent_at) {
    return '';
  }

  return dateTime.formatDateTimeWithTimeZone(row.sent_at);
}

export function formatEmailHistoryScheduledCell(
  row: Pick<EmailHistoryQuerySchema, 'scheduled_at' | 'sent_at'>
): string {
  if (!row.scheduled_at) {
    return '';
  }

  return dateTime.formatDateTimeWithTimeZone(row.scheduled_at);
}

/** @deprecated Prefer separate Sent / Scheduled columns via formatEmailHistorySentCell and formatEmailHistoryScheduledCell. */
export function formatEmailHistorySentOrScheduled(
  row: Pick<EmailHistoryQuerySchema, 'scheduled_at' | 'sent_at'>
): { label: string; value: string } | null {
  if (row.sent_at) {
    return {
      label: 'Sent',
      value: dateTime.formatDateTimeWithTimeZone(row.sent_at),
    };
  }

  if (row.scheduled_at) {
    return {
      label: 'Scheduled',
      value: dateTime.formatDateTimeWithTimeZone(row.scheduled_at),
    };
  }

  return null;
}

/** @deprecated Prefer separate Sent / Scheduled columns. */
export function formatEmailHistorySentOrScheduledCell(
  row: Pick<EmailHistoryQuerySchema, 'scheduled_at' | 'sent_at'>
): string {
  const formatted = formatEmailHistorySentOrScheduled(row);

  if (!formatted) {
    return '—';
  }

  if (formatted.label === 'Scheduled') {
    return `Scheduled ${formatted.value}`;
  }

  return formatted.value;
}
